import { InputAdornmentProps } from '@material-ui/core/InputAdornment';
import { PopoverProps as PopoverPropsType } from '@material-ui/core/Popover';
import { TextFieldProps } from '@material-ui/core/TextField';
import { CancelOutlined as CancelOutlinedIcon } from '@material-ui/icons';
import { KeyboardDatePicker, KeyboardDateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { FieldHelperText } from '@shared/components/field-helper-text';
import { Flex } from '@shared/components/flex';
import { Tooltip, TooltipProps } from '@shared/components/tooltip/Tooltip';
import { InputSize } from '@shared/types/common/input';
import { DATE_TIME_PICKER_FORMAT, formatTimestamp, isDateTextValid, PICKER_DATE_FORMAT } from '@shared/utils/date';

import { styles } from './DatePicker.styles';

export type MaterialDate = MaterialUiPickersDate | string | null;

export interface DatePickerProps extends WithStyles<typeof styles> {
  // Focuses the input on mount; the calendar opens only on a click
  autoFocus?: boolean;
  autoWidth?: boolean;
  dateFormat?: string;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  error?: boolean;
  errorText?: string;
  fullWidth?: boolean;
  label?: string;
  minDate?: MaterialDate | Date;
  placeholder: string;
  size?: InputSize;
  tooltip?: string;
  tooltipArrow?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
  type?: 'date' | 'dateAndTime';
  value: MaterialDate | Date;
  // Only complete dates, clears and calendar picks reach onChange; half-typed text stays in the box, and an emptied box
  // is sent as a clear once it is left
  onChange: (date: MaterialDate | null, value: string | null) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
}

const DatePickerComponent: React.FC<DatePickerProps> = ({
  autoFocus,
  autoWidth = true,
  classes,
  dateFormat,
  disabled = false,
  disableFuture = false,
  disablePast = false,
  error,
  errorText,
  fullWidth = false,
  label,
  minDate,
  placeholder,
  size = InputSize.mediumLegacy,
  tooltip,
  tooltipArrow,
  tooltipPlacement,
  type = 'date',
  value,
  onChange,
  onKeyDown,
}) => {
  const format = dateFormat || (type === 'date' ? PICKER_DATE_FORMAT : DATE_TIME_PICKER_FORMAT);
  const valueText = formatTimestamp(value, format);

  // Picks made while the calendar is open, so several parts of the date can be changed before any is sent
  const draftRef = useRef<{ date: MaterialUiPickersDate | null; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pointerDownRef = useRef(false);
  const refocusInputRef = useRef(false);
  const restoreOnMouseUpRef = useRef<(() => void) | null>(null);
  const valueTextRef = useRef(valueText);

  // Remounts the picker when the box goes back to empty, as the picker falls back to its own copy of the typed text
  const [inputKey, setInputKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(valueText);

  // Half-typed text, held in the box until it makes a whole date
  const textInvalid = text !== '' && !isDateTextValid(text, format);

  const className = cx(
    classes.picker,
    classes[size],
    { [classes.pickerFullWidth]: fullWidth },
    { [classes.pickedDisabled]: disabled }
  );

  // Configure date icon
  const InputAdornmentProps: Partial<InputAdornmentProps> = {
    position: 'start',
  };

  // A remount after leaving the box must not pull focus back into it
  const pickerAutoFocus = autoFocus && inputKey === 0;
  const pickerValue = valueText ? value : null;
  const pickerError = error || textInvalid;
  const pickerErrorText = textInvalid && !(error && errorText) ? t('invalid_date') : errorText;

  // Input props
  const inputProps: TextFieldProps['inputProps'] = { ref: inputRef };
  const InputProps: TextFieldProps['InputProps'] = {
    // Clear icon
    endAdornment: valueText ? (
      <CancelOutlinedIcon
        data-testid="date-picker-clear-icon"
        onClick={(e) => {
          e.stopPropagation();
          onChange(null, null);
        }}
        classes={{ root: classes.clearIcon }}
      />
    ) : undefined,
  };

  // Clicking away, Enter or picking a day in a date-only calendar sends the picks; Escape puts the last date back
  const closeCalendar = (commit: boolean) => {
    const draft = draftRef.current;

    draftRef.current = null;
    setOpen(false);

    if (!draft || draft.text === valueTextRef.current) {
      return;
    }

    if (commit && isDateTextValid(draft.text, format)) {
      onChange(draft.date, draft.text);
    } else {
      // Going back to empty rebuilds the box, so the focus the calendar hands back lands on the page instead
      refocusInputRef.current = !valueTextRef.current;
      restoreText();
    }
  };

  const closeCalendarRef = useRef(closeCalendar);
  closeCalendarRef.current = closeCalendar;

  // Popover props
  const PopoverProps: Partial<PopoverPropsType> = {
    onClose: (_event, reason) => closeCalendar(reason !== 'escapeKeyDown'),
  };
  const dateTimePopoverProps: Partial<PopoverPropsType> = {
    ...PopoverProps,
    classes: { paper: classes.dateTimePopover },
  };

  const pickerOnChange = (date: MaterialUiPickersDate | null, inputText?: string | null) => {
    setText(inputText ?? '');

    if (open) {
      draftRef.current = { date, text: inputText ?? '' };
    } else if (inputText && isDateTextValid(inputText, format)) {
      onChange(date, inputText);
    }
  };

  const restoreText = () => {
    restoreOnMouseUpRef.current = null;
    setText(valueTextRef.current);

    if (!valueTextRef.current) {
      setInputKey((current) => current + 1);
    }
  };

  // Leaving an emptied box clears the date, so backspacing it all leaves room to type a new one first. Leaving a
  // half-typed date puts the last good date back. When a mouse press left it, that waits for the release: hiding the
  // error message moves whatever is below the box, and the click would miss what was pressed
  const pickerOnBlur = () => {
    if (text === '' && valueText !== '') {
      onChange(null, null);
    } else if (textInvalid) {
      if (pointerDownRef.current) {
        if (restoreOnMouseUpRef.current) {
          window.removeEventListener('mouseup', restoreOnMouseUpRef.current);
        }

        restoreOnMouseUpRef.current = restoreText;
        window.addEventListener('mouseup', restoreText, { once: true });
      } else {
        restoreText();
      }
    }
  };

  // Parents may pass a new date object each render, so the held text follows the value only when its text changes
  useEffect(() => {
    valueTextRef.current = valueText;
    setText(valueText);
  }, [valueText]);

  // Only a mouse press can blur the box mid-click, so presses are tracked only while the text is half-typed
  useEffect(() => {
    if (!textInvalid) {
      return;
    }

    const handleMouseDown = () => {
      pointerDownRef.current = true;
    };
    const handleMouseUp = () => {
      pointerDownRef.current = false;
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      pointerDownRef.current = false;
    };
  }, [textInvalid]);

  useEffect(() => {
    if (refocusInputRef.current) {
      refocusInputRef.current = false;
      inputRef.current?.focus();
    }
  }, [inputKey]);

  // A pick that changes the calendar's view drops focus out of it, where the calendar no longer hears Escape
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCalendarRef.current(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    return () => {
      if (restoreOnMouseUpRef.current) {
        window.removeEventListener('mouseup', restoreOnMouseUpRef.current);
      }
    };
  }, []);

  const fieldContent =
    type === 'date' ? (
      <KeyboardDatePicker
        autoFocus={pickerAutoFocus}
        autoOk={true}
        className={className}
        disabled={disabled}
        disableFuture={disableFuture}
        disablePast={disablePast}
        error={pickerError}
        format={format}
        InputAdornmentProps={InputAdornmentProps}
        InputProps={InputProps}
        inputProps={inputProps}
        inputValue={text}
        inputVariant="outlined"
        invalidDateMessage=""
        key={inputKey}
        margin="none"
        minDate={minDate}
        open={open}
        placeholder={placeholder}
        PopoverProps={PopoverProps}
        size="small"
        value={pickerValue}
        variant="inline"
        onBlur={pickerOnBlur}
        onChange={pickerOnChange}
        onClick={() => setOpen(true)}
        onClose={() => closeCalendar(true)}
        onKeyDown={onKeyDown}
      />
    ) : (
      <KeyboardDateTimePicker
        autoFocus={pickerAutoFocus}
        className={className}
        disabled={disabled}
        disableFuture={disableFuture}
        disablePast={disablePast}
        error={pickerError}
        format={format}
        hideTabs={true}
        InputAdornmentProps={InputAdornmentProps}
        InputProps={InputProps}
        inputProps={inputProps}
        inputValue={text}
        inputVariant="outlined"
        invalidDateMessage=""
        key={inputKey}
        margin="none"
        minDate={minDate}
        open={open}
        placeholder={placeholder}
        PopoverProps={dateTimePopoverProps}
        size="small"
        value={pickerValue}
        variant="inline"
        onBlur={pickerOnBlur}
        onChange={pickerOnChange}
        onClick={() => setOpen(true)}
        onClose={() => closeCalendar(true)}
        onKeyDown={onKeyDown}
      />
    );

  const content = tooltip ? (
    <Tooltip arrow={tooltipArrow} placement={tooltipPlacement || undefined} title={tooltip}>
      {fieldContent}
    </Tooltip>
  ) : (
    fieldContent
  );

  return (
    <Flex autoWidth={autoWidth} direction="column" classes={{ root: classes.root }}>
      {label && <span className={classes.label}>{label}</span>}
      <Flex direction="column" autoWidth={false} classes={{ root: classes.inputContainer }}>
        {content}
        <FieldHelperText error={pickerError} errorText={pickerErrorText} />
      </Flex>
    </Flex>
  );
};

export const DatePicker = withStyles(styles)(DatePickerComponent);
